import { memo, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { AlertTriangle, Check, RotateCcw, X } from 'lucide-react';
import type { AssetCard as AssetCardT } from '@/lib/api/types';
import { cx, formatBytes } from '@/lib/format';
import { ApprovalDot, CountBadges, ProgressRing, TypeBadge } from './cardBadges';
import { RightsBadge } from './cardBadges'; // release2/C
import { IncompleteCorner } from './cardBadges'; // release2/B
import { InternalOnlyBadge } from './cardBadges'; // release3/B
import { useSpriteScrub } from './scrub/useSpriteScrub';
import { isPlaceholder, type GridItem, type PlaceholderCard } from './types';

export type CardHandlers = {
  onPointerDown?: (e: PointerEvent<HTMLElement>, index: number) => void;
  onClick: (e: MouseEvent<HTMLElement>, index: number) => void;
  onDoubleClick: (index: number) => void;
  onContextMenu?: (e: MouseEvent<HTMLElement>, index: number) => void;
  onDragStart?: (e: DragEvent<HTMLElement>, index: number) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>, index: number) => void;
  onRetryPlaceholder?: (clientUploadId: string) => void;
  onDismissPlaceholder?: (clientUploadId: string) => void;
  /** optional per-card decoration (search result hits, match regions) drawn over the media box. */
  extra?: (card: AssetCardT, w: number, h: number) => ReactNode;
};

export type AssetCardProps = {
  item: GridItem;
  index: number;
  w: number;
  h: number;
  selected: boolean;
  focused: boolean;
  showTitle: boolean;
  draggable: boolean;
  handlers: CardHandlers;
};

function srcSetOf(card: AssetCardT): { src: string | undefined; srcSet: string | undefined } {
  const t = card.thumbnails;
  const parts: string[] = [];
  if (t['256']) parts.push(`${t['256']} 256w`);
  if (t['512']) parts.push(`${t['512']} 512w`);
  if (t['1024']) parts.push(`${t['1024']} 1024w`);
  const src = t['512'] ?? t['256'] ?? t['1024'] ?? undefined;
  return { src, srcSet: parts.length ? parts.join(', ') : undefined };
}

const PLACEHOLDER_LABEL: Record<PlaceholderCard['status'], string> = {
  pending: 'Queued',
  hashing: 'Hashing',
  initiating: 'Starting',
  uploading: 'Uploading',
  completing: 'Finishing',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Failed',
  paused: 'Paused',
  cancelled: 'Cancelled',
  duplicate: 'Duplicate',
};

function AssetCardImpl({ item, index, w, h, selected, focused, showTitle, draggable, handlers }: AssetCardProps) {
  const card = item.card;
  const mediaRef = useRef<HTMLDivElement>(null);
  const scrubRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const ph = isPlaceholder(card) ? card : null;
  const placeholder = ph !== null;
  const asset = ph ? null : (card as AssetCardT);
  const scrubRefs = useMemo(() => ({ media: mediaRef, scrub: scrubRef, line: lineRef, time: timeRef }), []); // stable: the hook's callbacks depend on it (M6)
  const scrub = useSpriteScrub(asset ?? EMPTY_CARD, scrubRefs);
  const mediaH = h - (showTitle ? 36 : 0);
  const color = ph ? (ph.color ?? 'var(--lm-bg-sunken)') : (asset?.dominant_colors?.[0]?.hex ?? 'var(--lm-bg-sunken)');
  const title = card.title;
  const processing = asset ? asset.version_state !== 'ready' : ph!.status !== 'ready';

  return (
    <article
      role="gridcell"
      tabIndex={focused ? 0 : -1}
      aria-selected={selected}
      aria-label={title}
      data-asset-id={asset?.id}
      data-key={card.id}
      data-index={index}
      data-state={asset ? asset.version_state : ph!.status}
      draggable={draggable && !placeholder}
      className={cx('group relative h-full w-full cursor-default rounded-md outline-none select-none', selected && 'ring-2 ring-accent ring-offset-2 ring-offset-bg', focused && !selected && 'ring-1 ring-line-strong')}
      onPointerDown={(e) => handlers.onPointerDown?.(e, index)}
      onClick={(e) => {
        if (scrub.wasTouchScrubbing()) return;
        handlers.onClick(e, index);
      }}
      onDoubleClick={() => handlers.onDoubleClick(index)}
      onContextMenu={(e) => handlers.onContextMenu?.(e, index)}
      onDragStart={(e) => handlers.onDragStart?.(e, index)}
      onKeyDown={(e) => handlers.onKeyDown?.(e, index)}
    >
      <div
        ref={mediaRef}
        className="card-media relative overflow-hidden rounded-md"
        style={{ width: w, height: mediaH, background: color }}
        onPointerEnter={scrub.enabled ? scrub.onPointerEnter : undefined}
        onPointerMove={scrub.enabled ? scrub.onPointerMove : undefined}
        onPointerLeave={scrub.enabled ? scrub.onPointerLeave : undefined}
        onTouchStart={scrub.enabled ? scrub.onTouchStart : undefined}
        onTouchMove={scrub.enabled ? scrub.onTouchMove : undefined}
        onTouchEnd={scrub.enabled ? scrub.onTouchEnd : undefined}
      >
        {asset ? (
          <CardImage card={asset} width={w} />
        ) : (
          ph?.preview_url && <img src={ph.preview_url} alt="" decoding="async" draggable={false} className="h-full w-full object-contain" />
        )}
        <div ref={scrubRef} className="card-scrub" hidden />
        {scrub.enabled && <div ref={lineRef} className="card-scrub-line" />}
        {asset && processing && <ProgressRing progress={asset.processing_progress == null ? null : asset.processing_progress / 100} label={asset.version_state === 'failed' ? 'Failed' : 'Processing'} />}
        {ph && <PlaceholderOverlay card={ph} handlers={handlers} />}
        {asset && handlers.extra?.(asset, w, mediaH)}
        {asset && <TypeBadge type={asset.type} durationMs={asset.duration_ms} pageCount={asset.page_count} timeRef={timeRef} />}
        {asset && <IncompleteCorner incomplete={asset.incomplete} /> /* release2/B */}
        {asset && <ApprovalDot status={asset.approval_status} />}
        {asset && <RightsBadge status={asset.rights_status} expiresAt={asset.rights_expires_at} validFrom={asset.rights_valid_from} />} {/* release2/C; release4/R embargo */}
        {asset && <InternalOnlyBadge excluded={asset.share_excluded} />} {/* release3/B */}
        {asset && <CountBadges versions={asset.version_count} comments={asset.comment_count} unresolved={asset.unresolved_comment_count} />}
        {!placeholder && (
          <span
            role="checkbox"
            aria-checked={selected}
            aria-label={selected ? 'Deselect' : 'Select'}
            tabIndex={focused ? 0 : -1}
            onKeyDown={(e) => {
              // keyboard: Space/Enter toggle the selection without opening the asset (the card's own Enter opens)
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.click();
              }
            }}
            data-select-toggle
            className={cx('card-hover-only absolute top-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded border border-white/80 bg-black/40 text-white', selected && 'bg-accent border-accent')}
          >
            {selected && <Check size={14} />}
          </span>
        )}
      </div>
      {showTitle && (
        <footer className="relative flex h-9 items-center gap-2 px-1 text-xs">
          <span className="truncate text-fg" title={title}>
            {title}
          </span>
          {asset?.size_bytes != null && <span className="ml-auto shrink-0 text-fg-subtle">{formatBytes(asset.size_bytes)}</span>}
          {asset && <CardTags names={asset.tag_names} />}
        </footer>
      )}
    </article>
  );
}

/** Up to three manual tag chips, revealed on hover/focus. Absolutely positioned over the bottom of the title row so the layout never shifts. */
const CARD_TAGS_MAX = 3;
function CardTags({ names }: { names: string[] | undefined }) {
  if (!names || names.length === 0) return null;
  const shown = names.slice(0, CARD_TAGS_MAX);
  const more = names.length - shown.length;
  return (
    <div className="card-hover-only pointer-events-none absolute inset-x-1 bottom-0 flex items-center gap-1 overflow-hidden pb-0.5 text-[11px] leading-none" data-card-tags aria-hidden>
      {shown.map((n) => (
        <span key={n} className="max-w-[45%] truncate rounded-full border border-line bg-bg-elev px-1.5 py-0.5 text-fg-muted">
          {n}
        </span>
      ))}
      {more > 0 && <span className="text-fg-subtle">+{more}</span>}
    </div>
  );
}

const EMPTY_CARD = { id: '', type: 'other', version_state: 'ready', active_version_id: '', sprite_index_url: null, sprite_sheet_url: null, preview_mp4_url: null, duration_ms: 0 } as unknown as AssetCardT;

function CardImage({ card, width }: { card: AssetCardT; width: number }) {
  const { src, srcSet } = srcSetOf(card);
  // Versions processed before the worker wrote every size can lack thumb_512 / thumb_2048 although the api signs them.
  // On a load error drop the srcset (else the browser keeps picking the dead candidate) and walk the other URLs,
  // 1024 first: every thumbnailed version has it. Keyed on src, so a new card / re-signed URL starts over.
  const [failed, setFailed] = useState<{ src: string | undefined; step: number } | null>(null);
  const step = failed && failed.src === src ? failed.step : -1;
  const t = card.thumbnails;
  const fallbacks = [...new Set([t['1024'], t['256'], t['512']].filter((u): u is string => !!u))];
  if (!src || step >= fallbacks.length) return null;
  const onError = () => setFailed({ src, step: step + 1 });
  if (step < 0) return <img src={src} srcSet={srcSet} sizes={`${Math.ceil(width)}px`} alt="" decoding="async" loading="eager" draggable={false} onError={onError} />;
  return <img src={fallbacks[step]} alt="" decoding="async" loading="eager" draggable={false} onError={onError} />;
}

function PlaceholderOverlay({ card, handlers }: { card: PlaceholderCard; handlers: CardHandlers }) {
  if (card.status === 'failed' || card.status === 'cancelled') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 p-2 text-center text-white">
        <AlertTriangle size={20} className="text-warn" />
        <span className="truncate-2 text-xs">{card.error ?? PLACEHOLDER_LABEL[card.status]}</span>
        <div className="flex gap-1">
          {card.retryable && (
            <button type="button" className="flex items-center gap-1 rounded bg-white/15 px-2 py-0.5 text-xs hover:bg-white/25" onClick={(e) => (e.stopPropagation(), handlers.onRetryPlaceholder?.(card.client_upload_id))}>
              <RotateCcw size={11} /> Retry
            </button>
          )}
          <button type="button" className="flex items-center gap-1 rounded bg-white/15 px-2 py-0.5 text-xs hover:bg-white/25" onClick={(e) => (e.stopPropagation(), handlers.onDismissPlaceholder?.(card.client_upload_id))}>
            <X size={11} /> Dismiss
          </button>
        </div>
      </div>
    );
  }
  if (card.status === 'ready') return null;
  const determinate = card.status === 'uploading' || card.status === 'hashing' || card.status === 'processing';
  return <ProgressRing progress={determinate ? card.progress : null} label={card.status === 'uploading' ? `${Math.round(card.progress * 100)}%` : PLACEHOLDER_LABEL[card.status]} />;
}

function tagKeyOf(c: GridItem['card']): string {
  return isPlaceholder(c) ? '' : (c.tag_names?.slice(0, CARD_TAGS_MAX + 1).join('\u0000') ?? '');
}
function stateOf(c: GridItem['card']): string | number | null | undefined {
  return isPlaceholder(c) ? c.status : c.version_state;
}
function progressOf(c: GridItem['card']): number | null | undefined {
  return isPlaceholder(c) ? c.progress : c.processing_progress;
}

/**
 * Everything the card paints that a WS patch can change without bumping `updated_at`: comment counts,
 * approval, the active version (thumbnails, dims, duration) and the title (M4).
 */
function visualKeyOf(c: GridItem['card']): string {
  if (isPlaceholder(c)) return c.error ?? '';
  const t = c.thumbnails;
  return `${c.title}\u0000${c.comment_count}\u0000${c.unresolved_comment_count ?? ''}\u0000${c.approval_status}\u0000${c.active_version_id}\u0000${c.version_count}\u0000${t?.['512'] ?? ''}\u0000${t?.['256'] ?? ''}\u0000${c.preview_mp4_url ?? ''}`;
}

/** §2.5 memo comparator: (id, updated_at, state, progress, tag chips, counts/thumbs/version, selected, focused, w, h) + showTitle/handlers identity. */
export const AssetCard = memo(AssetCardImpl, (a, b) => {
  const ca = a.item.card;
  const cb = b.item.card;
  return (
    ca.id === cb.id &&
    ca.updated_at === cb.updated_at &&
    stateOf(ca) === stateOf(cb) &&
    progressOf(ca) === progressOf(cb) &&
    tagKeyOf(ca) === tagKeyOf(cb) &&
    visualKeyOf(ca) === visualKeyOf(cb) &&
    a.selected === b.selected &&
    a.focused === b.focused &&
    a.w === b.w &&
    a.h === b.h &&
    a.index === b.index &&
    a.showTitle === b.showTitle &&
    a.draggable === b.draggable &&
    a.handlers === b.handlers
  );
});
